
import { useState } from "react";
import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { api } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { JobStatusBadge, PaymentStatusBadge } from "@/components/features/badges";
import { LoadingState, ErrorState } from "@/components/features/states";
import { LiveJobStatus } from "@/components/features/live-status";
import { ReviewSection } from "@/components/features/review";
import { formatPrice, formatRelativeTime, currencySymbol } from "@/lib/format";
import { parseJobMetadata, type FoodMetadata, type GroceryMetadata, type PharmacyMetadata, type CourierMetadata, type ErrandMetadata } from "@/lib/types";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  MapPin,
  Package,
  MessageSquare,
  Check,
  Clock,
  Truck,
  Banknote,
  ShieldCheck,
  Star,
  Copy,
  Flag,
  Utensils,
  ShoppingCart,
  Receipt,
  Smartphone,
  Pill,
  FileText,
  ListChecks,
} from "lucide-react";

const TIMELINE: { key: string; labelKey: string }[] = [
  { key: "open", labelKey: "jobDetail.statusPosted" },
  { key: "assigned", labelKey: "status.assigned" },
  { key: "picked_up", labelKey: "status.picked_up" },
  { key: "in_transit", labelKey: "status.in_transit" },
  { key: "delivered", labelKey: "status.delivered" },
  { key: "completed", labelKey: "status.completed" },
];

function statusIndex(status: string): number {
  if (status === "negotiating") return 0;
  if (status === "cancelled") return -1;
  if (status === "disputed") return 5;
  return TIMELINE.findIndex((t) => t.key === status);
}

export function JobDetailView({ jobId }: { jobId: string }) {
  const { user } = useAuthStore();
  const navigate = useUiStore((s) => s.navigate);
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [confirmCode, setConfirmCode] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState("item_damaged");
  const [disputeDesc, setDisputeDesc] = useState("");
  // Phase 3: mobile money payment.
  const [payOpen, setPayOpen] = useState(false);
  const [mmProvider, setMmProvider] = useState<"dmoney" | "ebirr" | "zaad" | "waafi">("dmoney");
  const [mmPin, setMmPin] = useState("");
  const [paying, setPaying] = useState(false);
  const { data: mmAccountsData } = useQuery({
    queryKey: ["mobile-money-accounts"],
    queryFn: () => api.mobileMoney.list(),
  });
  const mmAccounts = mmAccountsData?.data?.accounts ?? [];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => api.jobs.get(jobId),
    refetchInterval: 5000,
  });
  const job = data?.data?.job;

  if (isLoading) return <LoadingState label={t("jobDetail.loadingOrder")} />;
  if (error || !data?.ok || !job)
    return <ErrorState message={data?.error || t("jobDetail.loadFailed")} onRetry={refetch} />;

  const isCustomer = job.customerId === user?.id;
  const isProvider = job.providerId === user?.id;
  const isAssignedProvider = isProvider;
  const tIdx = statusIndex(job.status);

  // Phase 2: parsed metadata + commission visibility.
  const foodMeta = job.parcelCategory === "food" ? parseJobMetadata<FoodMetadata>(job.metadata) : null;
  const groceryMeta = job.parcelCategory === "grocery" ? parseJobMetadata<GroceryMetadata>(job.metadata) : null;
  // Phase 3: new verticals metadata.
  const pharmacyMeta = job.parcelCategory === "pharmacy" ? parseJobMetadata<PharmacyMetadata>(job.metadata) : null;
  const courierMeta = job.parcelCategory === "courier" ? parseJobMetadata<CourierMetadata>(job.metadata) : null;
  const errandMeta = job.parcelCategory === "errand" ? parseJobMetadata<ErrandMetadata>(job.metadata) : null;
  const canSeeCommission =
    job.status === "completed" &&
    (user?.isAdmin || isCustomer || isAssignedProvider) &&
    (job.commissionAmount != null || job.providerPayout != null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["job", jobId] });
    qc.invalidateQueries({ queryKey: ["jobs"] });
  };

  // Direct courier accept (no offer bargaining). Assigns the open job to the
  // calling courier at the customer's stated delivery fee.
  const acceptJob = async () => {
    setAccepting(true);
    const res = await api.jobs.accept(jobId);
    setAccepting(false);
    if (!res.ok) return toast.error(res.error);
    toast.success(t("jobDetail.jobAcceptedToast"));
    invalidate();
  };

  const transition = async (status: string, extra?: any) => {
    const res = await api.jobs.transition(jobId, { status, ...extra });
    if (!res.ok) return toast.error(res.error);
    toast.success(t("jobDetail.statusUpdatedToast"));
    invalidate();
  };

  const collectPayment = async () => {
    if (confirmCode.length !== 4) return toast.error(t("jobDetail.enterCode"));
    const res = await api.payment.collect(jobId, { confirmationCode: confirmCode });
    if (!res.ok) return toast.error(res.error);
    toast.success(t("jobDetail.cashCollectedToast"));
    setConfirmCode("");
    invalidate();
  };

  const confirmReceipt = async () => {
    const res = await api.payment.confirm(jobId);
    if (!res.ok) return toast.error(res.error);
    toast.success(t("jobDetail.deliveryConfirmedToast"));
    invalidate();
  };

  // Phase 3: pay with mobile money.
  const payWithMobileMoney = async () => {
    if (mmPin.length < 4) return toast.error(t("mobileMoney.enterPin", { provider: mmProvider }));
    setPaying(true);
    const res = await api.mobileMoney.pay({ jobId, provider: mmProvider, pin: mmPin });
    setPaying(false);
    if (!res.ok) return toast.error(res.error || t("mobileMoney.paymentFailed"));
    setPayOpen(false);
    setMmPin("");
    toast.success(t("mobileMoney.paymentSuccess"));
    invalidate();
  };

  const openDispute = async () => {
    if (!disputeDesc.trim()) return toast.error(t("jobDetail.describeIssue"));
    const res = await api.disputes.create({ jobId, reason: disputeReason, description: disputeDesc.trim() });
    if (!res.ok) return toast.error(res.error);
    toast.success(t("jobDetail.disputeOpenedToast"));
    setDisputeOpen(false);
    setDisputeDesc("");
    invalidate();
  };

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate("app/jobs")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground">{job.code}</span>
            <JobStatusBadge status={job.status} />
            <LiveJobStatus jobId={job.id} />
          </div>
          <h1 className="line-clamp-1 text-lg font-bold">{job.parcelDescription}</h1>
        </div>
      </div>

      {/* Price + payment */}
      <Card>
        <CardContent className="flex items-center justify-between p-4">
          <div>
            <p className="text-xs text-muted-foreground">{job.agreedPrice ? t("jobDetail.agreedFee") : t("jobDetail.deliveryFee")}</p>
            <p className="text-2xl font-bold text-gold">{formatPrice(job.agreedPrice ?? job.customerOffer)}</p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1.5 text-sm font-medium">
              <Banknote className="h-4 w-4 text-emerald-600" /> {t("mobileMoney.cashOnDelivery")}
            </div>
            {job.payment && <div className="mt-1"><PaymentStatusBadge status={job.payment.status} /></div>}
          </div>
        </CardContent>
      </Card>

      {/* Status timeline */}
      {job.status !== "cancelled" && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              {TIMELINE.map((step, i) => {
                const done = tIdx >= 0 && i <= tIdx;
                const current = tIdx === i;
                return (
                  <div key={step.key} className="flex flex-1 flex-col items-center">
                    <div className="flex w-full items-center">
                      <div className={`h-0.5 flex-1 ${i === 0 ? "opacity-0" : done ? "bg-primary" : "bg-border"}`} />
                      <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition ${done ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"} ${current ? "ring-4 ring-primary/20" : ""}`}>
                        {done && !current ? <Check className="h-3.5 w-3.5" /> : i + 1}
                      </div>
                      <div className={`h-0.5 flex-1 ${i === TIMELINE.length - 1 ? "opacity-0" : tIdx > i ? "bg-primary" : "bg-border"}`} />
                    </div>
                    <span className={`mt-1.5 text-[10px] font-medium ${done ? "text-foreground" : "text-muted-foreground"}`}>{t(step.labelKey)}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Route */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex gap-3">
            <div className="flex flex-col items-center pt-1">
              <div className="h-3 w-3 rounded-full bg-emerald-500" />
              <div className="my-1 h-8 w-0.5 bg-border" />
              <div className="h-3 w-3 rounded-full bg-amber-500" />
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <p className="text-xs text-muted-foreground">{t("jobDetail.pickup")}</p>
                <p className="text-sm font-medium">{job.pickupAddress}</p>
                {job.pickupNotes && <p className="text-xs text-muted-foreground">{job.pickupNotes}</p>}
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t("jobDetail.dropoff")}</p>
                <p className="text-sm font-medium">{job.dropoffAddress}</p>
                {job.dropoffNotes && <p className="text-xs text-muted-foreground">{job.dropoffNotes}</p>}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 border-t border-border/60 pt-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Package className="h-3.5 w-3.5" /> {job.parcelCategory}</span>
            {job.parcelWeightKg && <span>· {job.parcelWeightKg} kg</span>}
            {job.parcelSize && <span className="capitalize">· {job.parcelSize}</span>}
            {job.fragile && <span className="text-amber-600">· {t("jobDetail.fragile")}</span>}
            <span className="capitalize">· {job.vehicleRequired} {t("jobDetail.vehicleRequiredSuffix")}</span>
            {job.contactPhone && <span>· {job.contactPhone}</span>}
          </div>
        </CardContent>
      </Card>

      {/* Phase 2: Food metadata */}
      {foodMeta && (
        <Card className="border-amber-300/50">
          <CardContent className="space-y-3 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Utensils className="h-4 w-4 text-amber-600" /> {t("jobDetail.restaurantOrder")}
            </div>
            {foodMeta.restaurantName && (
              <p className="text-sm">
                <span className="text-muted-foreground">{t("jobDetail.from")} </span>
                <span className="font-medium">{foodMeta.restaurantName}</span>
              </p>
            )}
            {foodMeta.orderItems && foodMeta.orderItems.length > 0 && (
              <div className="space-y-1.5 rounded-lg bg-muted/40 p-3 text-sm">
                {foodMeta.orderItems.map((it, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="font-medium">
                      {it.name} <span className="text-muted-foreground">× {it.qty}</span>
                    </span>
                    <span className="text-muted-foreground">{formatPrice(it.price)}</span>
                  </div>
                ))}
              </div>
            )}
            {foodMeta.orderTotal != null && (
              <div className="flex items-center justify-between border-t border-border/60 pt-2 text-sm">
                <span className="text-muted-foreground">{t("jobDetail.orderTotal")}</span>
                <span className="font-bold text-gold">{formatPrice(foodMeta.orderTotal)}</span>
              </div>
            )}
            {foodMeta.specialInstructions && (
              <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                <span className="font-semibold">{t("jobDetail.note")} </span>{foodMeta.specialInstructions}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Phase 2: Grocery metadata */}
      {groceryMeta && (
        <Card className="border-amber-300/50">
          <CardContent className="space-y-3 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <ShoppingCart className="h-4 w-4 text-amber-600" /> {t("jobDetail.shoppingList")}
            </div>
            {groceryMeta.storeName && (
              <p className="text-sm">
                <span className="text-muted-foreground">{t("jobDetail.store")} </span>
                <span className="font-medium">{groceryMeta.storeName}</span>
              </p>
            )}
            {groceryMeta.items && groceryMeta.items.length > 0 && (
              <div className="space-y-1.5 rounded-lg bg-muted/40 p-3 text-sm">
                {groceryMeta.items.map((it, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="font-medium">
                      {it.name} <span className="text-muted-foreground">× {it.qty}</span>
                    </span>
                    <span className="text-muted-foreground">~ {formatPrice(it.estimatedPrice)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-xs">
              {groceryMeta.estimatedTotal != null && (
                <div className="rounded-lg bg-muted/40 p-2">
                  <p className="text-muted-foreground">{t("jobDetail.estimatedTotal")}</p>
                  <p className="font-bold text-gold">{formatPrice(groceryMeta.estimatedTotal)}</p>
                </div>
              )}
              <div className="rounded-lg bg-muted/40 p-2">
                <p className="text-muted-foreground">{t("jobDetail.substitutions")}</p>
                <p className="font-medium">{groceryMeta.substitutionsAllowed ? t("jobDetail.substitutionsYes") : t("jobDetail.substitutionsNo")}</p>
              </div>
              {groceryMeta.shoppingWindow && (
                <div className="rounded-lg bg-muted/40 p-2">
                  <p className="text-muted-foreground">{t("jobDetail.shoppingWindow")}</p>
                  <p className="font-medium">{groceryMeta.shoppingWindow}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Phase 3: Pharmacy metadata */}
      {pharmacyMeta && (
        <Card className="border-rose-300/50">
          <CardContent className="space-y-3 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Pill className="h-4 w-4 text-rose-600" /> {t("jobDetail.pharmacyOrder")}
            </div>
            {pharmacyMeta.pharmacyName && (
              <p className="text-sm">
                <span className="text-muted-foreground">{t("jobCreate.pharmacyName")}: </span>
                <span className="font-medium">{pharmacyMeta.pharmacyName}</span>
              </p>
            )}
            {pharmacyMeta.medicineItems && pharmacyMeta.medicineItems.length > 0 && (
              <div className="space-y-1.5 rounded-lg bg-muted/40 p-3 text-sm">
                {pharmacyMeta.medicineItems.map((it, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="font-medium">{it.name}</span>
                    <span className="text-muted-foreground">× {it.qty}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-lg bg-muted/40 p-2">
                <p className="text-muted-foreground">{t("jobDetail.prescription")}</p>
                <p className="font-medium">
                  {pharmacyMeta.prescriptionRequired ? t("jobDetail.prescriptionRequired") : t("jobDetail.prescriptionNotRequired")}
                </p>
              </div>
              {pharmacyMeta.prescriptionNumber && (
                <div className="rounded-lg bg-muted/40 p-2">
                  <p className="text-muted-foreground">{t("jobCreate.prescriptionNumber")}</p>
                  <p className="font-medium">{pharmacyMeta.prescriptionNumber}</p>
                </div>
              )}
              <div className="rounded-lg bg-muted/40 p-2">
                <p className="text-muted-foreground">{t("jobDetail.temperatureControlled")}</p>
                <p className="font-medium">{pharmacyMeta.temperatureControlled ? t("common.yes") : t("common.no")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Phase 3: Courier metadata */}
      {courierMeta && (
        <Card className="border-sky-300/50">
          <CardContent className="space-y-2 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <FileText className="h-4 w-4 text-sky-600" /> {t("jobDetail.courierTask")}
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-lg bg-muted/40 p-2">
                <p className="text-muted-foreground">{t("jobDetail.documentType")}</p>
                <p className="font-medium capitalize">
                  {courierMeta.documentType === "contract" ? t("jobCreate.documentTypeContract")
                    : courierMeta.documentType === "legal" ? t("jobCreate.documentTypeLegal")
                      : courierMeta.documentType === "bank" ? t("jobCreate.documentTypeBank")
                        : t("jobCreate.documentTypeOther")}
                </p>
              </div>
              <div className="rounded-lg bg-muted/40 p-2">
                <p className="text-muted-foreground">{t("jobDetail.urgency")}</p>
                <p className="font-medium">
                  {courierMeta.urgency === "normal" ? t("jobCreate.urgencyNormal")
                    : courierMeta.urgency === "urgent" ? t("jobCreate.urgencyUrgent")
                      : t("jobCreate.urgencySameDay")}
                </p>
              </div>
              <div className="rounded-lg bg-muted/40 p-2">
                <p className="text-muted-foreground">{t("jobDetail.signatureRequired")}</p>
                <p className="font-medium">{courierMeta.signatureRequired ? t("common.yes") : t("common.no")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Phase 3: Errand metadata */}
      {errandMeta && (
        <Card className="border-violet-300/50">
          <CardContent className="space-y-2 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <ListChecks className="h-4 w-4 text-violet-600" /> {t("jobDetail.errandTask")}
            </div>
            <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
              <div className="rounded-lg bg-muted/40 p-2">
                <p className="text-muted-foreground">{t("jobDetail.errandType")}</p>
                <p className="font-medium">
                  {errandMeta.errandType === "bill" ? t("jobCreate.errandTypeBill")
                    : errandMeta.errandType === "pickup" ? t("jobCreate.errandTypePickup")
                      : errandMeta.errandType === "queue" ? t("jobCreate.errandTypeQueue")
                        : t("jobCreate.errandTypeOther")}
                </p>
              </div>
              {errandMeta.errandLocation && (
                <div className="rounded-lg bg-muted/40 p-2">
                  <p className="text-muted-foreground">{t("jobDetail.errandLocation")}</p>
                  <p className="font-medium">{errandMeta.errandLocation}</p>
                </div>
              )}
            </div>
            {errandMeta.errandNotes && (
              <p className="rounded-lg bg-violet-50 p-2 text-xs text-violet-800 dark:bg-violet-950/30 dark:text-violet-200">
                {errandMeta.errandNotes}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Phase 2: Commission summary (admin/provider/customer, completed only) */}
      {canSeeCommission && (
        <Card className="border-emerald-300/50 bg-emerald-50/50 dark:bg-emerald-950/20">
          <CardContent className="flex items-center gap-3 p-4">
            <Receipt className="h-5 w-5 shrink-0 text-emerald-600" />
            <div className="flex-1 text-sm">
              <p className="font-semibold text-emerald-900 dark:text-emerald-200">{t("jobDetail.commissionSummary")}</p>
              <p className="text-xs text-emerald-700 dark:text-emerald-300">
                {t("jobDetail.commission")}:{" "}
                <span className="font-medium">
                  {formatPrice(job.commissionAmount ?? 0)}
                </span>{" "}
                · {t("jobDetail.providerPayout")}:{" "}
                <span className="font-medium">
                  {formatPrice(job.providerPayout ?? job.agreedPrice ?? job.customerOffer ?? 0)}
                </span>
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmation code (revealed at assignment) */}
      {job.confirmationCode && (isCustomer || isAssignedProvider) && job.status !== "completed" && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="flex items-center gap-3 p-4">
            <ShieldCheck className="h-8 w-8 shrink-0 text-primary" />
            <div className="flex-1">
              <p className="text-sm font-semibold">{t("jobDetail.confirmationCodeTitle")}</p>
              <p className="text-xs text-muted-foreground">
                {isCustomer ? t("jobDetail.shareCodeCustomer") : t("jobDetail.enterCodeProvider")}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded-lg bg-background px-3 py-1.5 font-mono text-xl font-bold tracking-widest text-primary">
                {job.confirmationCode}
              </span>
              {isCustomer && (
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { navigator.clipboard?.writeText(job.confirmationCode!); toast.success(t("jobDetail.codeCopiedToast")); }}>
                  <Copy className="h-4 w-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Parties */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <PartyRow label={t("jobDetail.customer")} user={job.customer} />
          {job.provider && <PartyRow label={t("jobDetail.courier")} user={job.provider} />}
        </CardContent>
      </Card>

      {/* Action area */}
      <div className="space-y-3">
        {/* Courier: accept job directly (no offer bargaining) */}
        {!isCustomer && job.status === "open" && (
          <Card className="border-primary/30">
            <CardContent className="space-y-3 p-4">
              <p className="text-xs text-muted-foreground">{t("jobDetail.deliveryFee")}</p>
              <p className="text-2xl font-bold text-gold">{formatPrice(job.customerOffer)}</p>
              <Button className="w-full" size="lg" onClick={acceptJob} disabled={accepting}>
                <Check className="mr-2 h-4 w-4" /> {accepting ? t("jobDetail.accepting") : t("jobDetail.acceptThisJob")}
              </Button>
              <p className="text-[11px] text-muted-foreground">{t("jobDetail.acceptHint")}</p>
            </CardContent>
          </Card>
        )}
        {/* Customer: waiting for a courier to accept */}
        {isCustomer && job.status === "open" && (
          <div className="rounded-lg bg-muted/40 p-3 text-center text-sm text-muted-foreground">
            <Clock className="mx-auto mb-1 h-5 w-5" />
            {t("jobDetail.waitingForCourier")}
          </div>
        )}

        {/* Provider delivery actions */}
        {isAssignedProvider && job.status === "assigned" && (
          <Button className="w-full" size="lg" onClick={() => transition("picked_up")}>
            <Package className="mr-2 h-4 w-4" /> {t("jobDetail.markPickedUp")}
          </Button>
        )}
        {isAssignedProvider && job.status === "picked_up" && (
          <Button className="w-full" size="lg" onClick={() => transition("in_transit")}>
            <Truck className="mr-2 h-4 w-4" /> {t("jobDetail.startDelivery")}
          </Button>
        )}
        {isAssignedProvider && job.status === "in_transit" && (
          <Card className="border-primary/30">
            <CardContent className="space-y-3 p-4">
              <p className="text-sm font-semibold">{t("jobDetail.markDeliveredCollect")}</p>
              <p className="text-xs text-muted-foreground">{t("jobDetail.enterCustomerCode")}</p>
              <div className="flex gap-2">
                <Input inputMode="numeric" maxLength={4} placeholder="0000" className="text-center font-mono text-lg tracking-widest" value={confirmCode} onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, ""))} />
                <Button onClick={collectPayment} disabled={confirmCode.length !== 4}>{t("jobDetail.collectDeliver")}</Button>
              </div>
            </CardContent>
          </Card>
        )}
        {isAssignedProvider && job.status === "delivered" && (
          <div className="rounded-lg bg-amber-50 p-3 text-center text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
            {t("jobDetail.waitingForConfirmation")}
          </div>
        )}

        {/* Customer: confirm receipt */}
        {isCustomer && job.status === "delivered" && (
          <Button className="w-full" size="lg" onClick={confirmReceipt}>
            <Check className="mr-2 h-4 w-4" /> {t("jobDetail.confirmReceipt")}
          </Button>
        )}

        {/* Payment methods — Pay Cash (default COD) or Mobile Money
            (D-money, Waafi, eBirr, Zaad). Shown to the customer once a
            courier is assigned and payment is not yet confirmed. */}
        {isCustomer &&
          ["assigned", "picked_up", "in_transit", "delivered"].includes(job.status) &&
          job.payment?.status !== "confirmed" && (
            <Card>
              <CardContent className="space-y-3 p-4">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Banknote className="h-4 w-4 text-primary" /> {t("mobileMoney.paymentMethods")}
                </div>
                {/* Method chooser: Cash + mobile money providers */}
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {/* Pay Cash — default */}
                  <button
                    onClick={() => setPayOpen(false)}
                    className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-xs transition ${
                      !payOpen ? "border-primary bg-primary/5" : "border-border"
                    }`}
                  >
                    <Banknote className="h-4 w-4 text-emerald-600" />
                    <span className="font-semibold">{t("mobileMoney.payCash")}</span>
                    <span className="text-[9px] text-muted-foreground">{t("mobileMoney.payCashDesc")}</span>
                  </button>
                  {/* Mobile money providers */}
                  {(["dmoney", "waafi", "ebirr", "zaad"] as const).map((p) => {
                    const linked = mmAccounts.find((a) => a.provider === p);
                    const label =
                      p === "dmoney" ? t("mobileMoney.dmoney")
                        : p === "waafi" ? t("mobileMoney.waafi")
                        : p === "ebirr" ? t("mobileMoney.ebirr")
                        : t("mobileMoney.zaad");
                    return (
                      <button
                        key={p}
                        disabled={!linked}
                        onClick={() => { setMmProvider(p); setPayOpen(true); }}
                        className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-xs transition ${
                          payOpen && mmProvider === p ? "border-primary bg-primary/5" : "border-border"
                        } ${!linked ? "opacity-40" : ""}`}
                      >
                        <Smartphone className="h-4 w-4" />
                        <span className="font-semibold">{label}</span>
                        {linked ? (
                          <span className="text-[9px] text-emerald-600">✓ {t("mobileMoney.linked")}</span>
                        ) : (
                          <span className="text-[9px] text-muted-foreground">{t("mobileMoney.notLinked")}</span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Cash info note */}
                {!payOpen && (
                  <div className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
                    {t("mobileMoney.cashNote")}
                  </div>
                )}

                {/* Mobile money PIN entry + pay */}
                {payOpen && (
                  <div className="space-y-3 border-t border-border pt-3">
                    <div className="rounded-lg bg-muted/50 px-3 py-2 text-center text-sm">
                      {t("mobileMoney.confirmPayment", {
                        amount: new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(job.agreedPrice ?? job.customerOffer),
                        currency: currencySymbol(user?.countryCode),
                      })}
                    </div>
                    {mmAccounts.length === 0 ? (
                      <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-3 text-center text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
                        {t("mobileMoney.noAccounts")}
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2 w-full"
                          onClick={() => navigate("app/mobile-money")}
                        >
                          {t("mobileMoney.linkAccount")}
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium">
                            {t("mobileMoney.enterPin", {
                              provider:
                                mmProvider === "dmoney" ? t("mobileMoney.dmoney")
                                  : mmProvider === "waafi" ? t("mobileMoney.waafi")
                                  : mmProvider === "ebirr" ? t("mobileMoney.ebirr")
                                  : t("mobileMoney.zaad"),
                            })}
                          </label>
                          <Input
                            type="password"
                            inputMode="numeric"
                            maxLength={6}
                            placeholder="••••"
                            className="text-center font-mono tracking-widest"
                            value={mmPin}
                            onChange={(e) => setMmPin(e.target.value.replace(/\D/g, ""))}
                          />
                        </div>
                        <p className="text-[11px] text-muted-foreground">{t("mobileMoney.demoSimulated")}</p>
                        <div className="flex gap-2">
                          <Button variant="outline" className="flex-1" onClick={() => { setPayOpen(false); setMmPin(""); }}>
                            {t("common.cancel")}
                          </Button>
                          <Button className="flex-1" onClick={payWithMobileMoney} disabled={paying || mmPin.length < 4}>
                            {paying ? t("mobileMoney.paying") : t("mobileMoney.payNow")}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

        {/* Chat button */}
        {job.providerId && (isCustomer || isProvider) && (
          <Button variant="outline" className="w-full" size="lg" onClick={() => navigate(`app/chat/${jobId}`)}>
            <MessageSquare className="mr-2 h-4 w-4" /> {t("jobDetail.openChatWith")} {isCustomer ? t("jobDetail.courier") : t("jobDetail.customer")}
          </Button>
        )}

        {/* Dispute / cancel */}
        {isCustomer && job.status === "open" && (
          <Button variant="ghost" className="w-full text-muted-foreground" onClick={() => transition("cancelled", { cancelReason: t("jobDetail.cancelJob") })}>
            {t("jobDetail.cancelJob")}
          </Button>
        )}
        {(isCustomer || isProvider) && ["assigned", "picked_up", "in_transit", "delivered"].includes(job.status) && (
          <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" className="w-full text-destructive">
                <Flag className="mr-2 h-4 w-4" /> {t("jobDetail.openDispute")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("jobDetail.openDispute")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">{t("jobDetail.reason")}</label>
                  <select className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)}>
                    <option value="item_damaged">{t("jobDetail.reasonItemDamaged")}</option>
                    <option value="not_delivered">{t("jobDetail.reasonNotDelivered")}</option>
                    <option value="overcharged">{t("jobDetail.reasonOvercharged")}</option>
                    <option value="other">{t("jobDetail.reasonOther")}</option>
                  </select>
                </div>
                <Textarea placeholder={t("jobDetail.describeWhatHappenedPlaceholder")} rows={3} value={disputeDesc} onChange={(e) => setDisputeDesc(e.target.value)} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDisputeOpen(false)}>{t("common.cancel")}</Button>
                <Button variant="destructive" onClick={openDispute}>{t("jobDetail.submitDispute")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {job.status === "completed" && (
          <div className="rounded-lg bg-emerald-50 p-4 text-center dark:bg-emerald-950/30">
            <Check className="mx-auto mb-1 h-6 w-6 text-emerald-600" />
            <p className="font-semibold text-emerald-800 dark:text-emerald-200">{t("jobDetail.deliveryCompleted")}</p>
            <p className="text-xs text-emerald-700 dark:text-emerald-300">{t("jobDetail.paymentConfirmed", { amount: formatPrice(job.agreedPrice ?? job.customerOffer) })}</p>
          </div>
        )}

        {/* Phase 6: Rating & Review section (visible when job is completed or delivered) */}
        {(job.status === "completed" || job.status === "delivered") && job.providerId && (
          <ReviewSection
            jobId={job.id}
            targetType={isCustomer ? "courier" : "customer"}
            targetLabel={isCustomer ? (job.provider?.name || t("nav.courier") || "Courier") : (job.customer?.name || t("nav.customer") || "Customer")}
            canReview={isCustomer || isAssignedProvider}
          />
        )}
        {job.status === "cancelled" && (
          <div className="rounded-lg bg-muted p-4 text-center">
            <p className="font-semibold">{t("jobDetail.jobCancelled")}</p>
            {job.cancelReason && <p className="text-xs text-muted-foreground">{job.cancelReason}</p>}
          </div>
        )}
        {job.status === "disputed" && (
          <div className="rounded-lg bg-red-50 p-4 text-center dark:bg-red-950/30">
            <Flag className="mx-auto mb-1 h-6 w-6 text-red-600" />
            <p className="font-semibold text-red-800 dark:text-red-200">{t("jobDetail.underDispute")}</p>
            <p className="text-xs text-red-700 dark:text-red-300">{t("jobDetail.reviewingCase")}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function PartyRow({ label, user }: { label: string; user: any }) {
  return (
    <div className="flex items-center gap-3">
      <Avatar className="h-10 w-10">
        <AvatarFallback className="bg-primary/10 text-xs font-bold text-primary">{user.name.slice(0, 2).toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="flex-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="font-medium">{user.name}</p>
      </div>
      <div className="flex items-center gap-1 text-sm">
        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
        <span className="font-medium">{user.rating.toFixed(1)}</span>
        <span className="text-xs text-muted-foreground">({user.ratingCount})</span>
      </div>
    </div>
  );
}


